import { useEffect, useState } from "react";
import Button from "shared/Button";
import Alert from "shared/Alert";
import Modal from "shared/Modal";
import { showSuccess } from "shared/toast";
import { FORM_CONTROL } from "shared/tableStyles";
import { cn } from "lib/utils";
import { apiErrorMessage } from "../apiError";
import { getPrimeCompany, type PrimeContactWithAppearanceCount } from "../api/CrmApi";
import {
  postConversationMessage, postConversationOutcome, postConversationRedirect,
  type Channel, type Conversation, type ConversationOutcome,
} from "../api/OutreachTouchesApi";
import { announceDueChange } from "../modal/useDueCounts";
import AddPrimeContactModal from "./AddPrimeContactModal";
import ReceivedAtField, { receivedAtIso } from "./ReceivedAtField";

export const OUTCOME_LABEL: Record<ConversationOutcome, string> = {
  interested: "Interested", redirected: "Redirected", not_interested: "Not interested", teaming: "Teaming", other: "Other",
};
/** The results a person can choose. "Other" is no longer offered (it said nothing that "Open" does not) and a redirect has its own button;
 *  both still display on conversations that already have them. */
const CHOOSABLE: ConversationOutcome[] = ["interested", "not_interested", "teaming"];

interface Props {
  conversation: Conversation;
  /** Called after a change has been saved, with the fresh conversation when the server sent one (a redirect sends the NEW one). */
  onChanged: (updated?: Conversation, kind?: "redirect") => void;
}

/** What a person can do to a conversation: log a message, set the result (Open, Interested, Not interested, Teaming), reopen, redirect it to a
 *  colleague, or record that they asked to be removed. Used by the Today card and the Conversations inbox, so both work the same way. */
export default function ConversationActions({ conversation: c, onChanged }: Props) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | "teaming" | "remove" | "redirect">(null);

  /** Run one change, tell the page, show a failure inline. */
  async function change<T extends Conversation | unknown>(fn: () => Promise<T>, done?: string, kind?: "redirect"): Promise<boolean> {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (done) showSuccess(done);
      announceDueChange();
      onChanged((result as Conversation)?.messages ? (result as Conversation) : undefined, kind);
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that. Reload and try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function pickResult(value: string) {
    if (value === "teaming") return setDialog("teaming");
    if (value === "not_interested") { void change(() => postConversationOutcome(c.id, { outcome: "not_interested" }), "Closed as not interested"); return; }
    void change(() => postConversationOutcome(c.id, { outcome: (value || null) as ConversationOutcome | null }), "Saved");
  }

  const who = c.contact_name || c.contact_email || "Unknown contact";
  const legacy = c.outcome && !CHOOSABLE.includes(c.outcome) ? c.outcome : null;       // "other" or "redirected" on an older conversation
  return (
    <div className="space-y-3">
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {c.state !== "closed" ? <Composer conversation={c} busy={busy} change={change} /> : (
        <p className="text-xs text-muted-foreground">Closed{c.outcome ? ` as ${OUTCOME_LABEL[c.outcome].toLowerCase()}` : ""}. A new message from them reopens it.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`outcome-${c.id}`} className="text-xs font-medium text-muted-foreground">Where it stands</label>
        <select id={`outcome-${c.id}`} value={c.outcome ?? ""} disabled={busy || legacy === "redirected"} onChange={(e) => pickResult(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`}>
          <option value="">Open</option>
          {legacy && <option value={legacy} disabled>{OUTCOME_LABEL[legacy]}</option>}
          {CHOOSABLE.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
        </select>
        {c.state === "closed" && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void change(() => postConversationOutcome(c.id, { outcome: null }), "Reopened")}>Reopen</Button>
        )}
        {c.state !== "closed" && <Button variant="outline" size="sm" disabled={busy} onClick={() => setDialog("redirect")}>Redirect to a colleague</Button>}
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setDialog("remove")}>They asked to be removed</Button>
      </div>

      {dialog === "teaming" && (
        <TeamingDialog
          who={who} company={c.company_name} busy={busy} onCancel={() => setDialog(null)}
          onConfirm={async () => { const ok = await change(() => postConversationOutcome(c.id, { outcome: "teaming" }), "Marked as teaming"); if (ok) setDialog(null); }}
        />
      )}
      {dialog === "remove" && (
        <RemoveDialog
          who={who} busy={busy} onCancel={() => setDialog(null)}
          onConfirm={async (reason) => {
            const ok = await change(() => postConversationOutcome(c.id, { outcome: "not_interested", remove_requested: true, reason }), "Closed and marked Do Not Contact");
            if (ok) setDialog(null);
          }}
        />
      )}
      {dialog === "redirect" && (
        <RedirectDialog
          conversation={c} busy={busy} onCancel={() => setDialog(null)}
          onPick={async (id) => { const ok = await change(() => postConversationRedirect(c.id, id), "Started a conversation with the new contact", "redirect"); if (ok) setDialog(null); }}
        />
      )}
    </div>
  );
}

function Composer({ conversation, busy, change }: { conversation: Conversation; busy: boolean; change: <T>(fn: () => Promise<T>, done?: string) => Promise<boolean> }) {
  const [sender, setSender] = useState<"contact" | "us">("contact");
  const [channel, setChannel] = useState<Channel>("linkedin");
  const [text, setText] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  async function add() {
    if (!text.trim()) return;
    const at = sender === "contact" ? receivedAtIso(receivedAt) : undefined;
    const ok = await change(() => postConversationMessage(conversation.id, { sender, channel, text: text.trim(), ...(at ? { received_at: at } : {}) }), "Message added");
    if (ok) { setText(""); setReceivedAt(""); }
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Who wrote it">
        {([["contact", "They replied"], ["us", "We replied"]] as const).map(([v, label]) => (
          <button key={v} type="button" aria-pressed={sender === v} onClick={() => setSender(v)}
            className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium", sender === v ? "border-primary text-primary bg-primary-soft" : "border-border text-muted-foreground bg-surface")}>
            {label}
          </button>
        ))}
        <select aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className={`px-3 py-1.5 ${FORM_CONTROL}`}>
          <option value="linkedin">LinkedIn</option>
          <option value="email">Email</option>
        </select>
      </div>
      <textarea aria-label="Message text" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={20000}
        placeholder={sender === "contact" ? "Paste what they wrote" : "Paste what we sent"} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
      {sender === "contact" && <ReceivedAtField id={`received-${conversation.id}`} value={receivedAt} onChange={setReceivedAt} disabled={busy} />}
      {sender === "contact" && <p className="text-xs text-muted-foreground">Counts as a reply on this RFP and stops anything still planned for this company. Don't log the same reply twice.</p>}
      <Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={add}>Add to thread</Button>
    </div>
  );
}

function TeamingDialog({ who, company, busy, onCancel, onConfirm }: { who: string; company: string | null; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <Modal
      size="md" title="Mark as teaming" onClick={onCancel} closeButtonCLick={onCancel}
      footer={<>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={busy} onClick={onConfirm}>{busy ? "Saving…" : "Mark as teaming"}</Button>
      </>}
    >
      <div className="space-y-3 text-sm">
        <p>{who} and {company ?? "their company"} are teaming up with us. The conversation closes.</p>
        <p><strong>{company ?? "This company"} will not be contacted in any future campaign.</strong> Nobody there gets a cold email or LinkedIn message, and no hand-off is asked for. You can lift this by setting the conversation back to Open.</p>
      </div>
    </Modal>
  );
}

function RemoveDialog({ who, busy, onCancel, onConfirm }: { who: string; busy: boolean; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Modal
      size="md" title="They asked to be removed" onClick={onCancel} closeButtonCLick={onCancel}
      footer={<>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>{busy ? "Saving…" : "Mark Do Not Contact"}</Button>
      </>}
    >
      <div className="space-y-3 text-sm">
        <p>{who} asked to be taken off our list. The conversation closes as not interested and <strong>{who}</strong> is marked <strong>Do Not Contact</strong>: nothing further is sent to them, on any RFP. Colleagues at the same company are not affected.</p>
        <div>
          <label htmlFor="dnc-reason" className="block text-xs font-medium text-muted-foreground mb-1.5">Reason (required)</label>
          <textarea id="dnc-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
        </div>
      </div>
    </Modal>
  );
}

function RedirectDialog({ conversation, busy, onCancel, onPick }: { conversation: Conversation; busy: boolean; onCancel: () => void; onPick: (id: number) => void }) {
  const [people, setPeople] = useState<PrimeContactWithAppearanceCount[] | null>(null);
  const [picked, setPicked] = useState<number | "">("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    getPrimeCompany(conversation.prime_company_id)
      .then((co) => { if (live) setPeople((co.prime_contacts ?? []).filter((p) => p.id !== conversation.prime_contact_id && p.status === "active" && (p.first_name ?? "").trim())); })
      .catch((e) => { if (live) { setPeople([]); setError(apiErrorMessage(e, "Could not load the company's contacts.")); } });
    return () => { live = false; };
  }, [conversation.prime_company_id, conversation.prime_contact_id]);
  return (
    <>
      <Modal
        size="md" title="They pointed us to a colleague" onClick={onCancel} closeButtonCLick={onCancel}
        footer={<>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={busy || picked === ""} onClick={() => picked !== "" && onPick(picked)}>{busy ? "Saving…" : "Start conversation"}</Button>
        </>}
      >
        <div className="space-y-3 text-sm">
          <p>This conversation closes as redirected, and a new one starts with the colleague. Nothing is sent to them automatically.</p>
          {error && <Alert tone="danger" role="alert">{error}</Alert>}
          <label htmlFor="redirect-to" className="block text-xs font-medium text-muted-foreground">Colleague at {conversation.company_name}</label>
          <select id="redirect-to" value={picked} onChange={(e) => setPicked(e.target.value ? Number(e.target.value) : "")} className={`w-full px-3 py-2 ${FORM_CONTROL}`}>
            <option value="">{people === null ? "Loading…" : "Choose a contact"}</option>
            {(people ?? []).map((p) => <option key={p.id} value={p.id}>{[p.first_name, p.last_name].filter(Boolean).join(" ")} ({p.primary_email})</option>)}
          </select>
          <button type="button" className="text-sm font-medium text-primary underline underline-offset-2" onClick={() => setAdding(true)}>Add a new contact</button>
        </div>
      </Modal>
      {adding && (
        <AddPrimeContactModal
          company={{ id: conversation.prime_company_id, name: conversation.company_name }}
          onClose={() => setAdding(false)}
          onSaved={(contact) => { setAdding(false); setPeople((prev) => [...(prev ?? []), contact as PrimeContactWithAppearanceCount]); setPicked(contact.id); }}
        />
      )}
    </>
  );
}
